import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './index.css';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import Achievements from './components/Achievements';
import Experience from './components/Experience';
// import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import BlogList from './components/BlogList';
import BlogDetail from './components/BlogDetail';
import { useScrollY } from './hooks/useScrollY';
import { ThemeProvider } from './contexts/ThemeContext';
import { useTheme } from './hooks/useTheme';

const HomePage: React.FC = () => {
  const scrollY = useScrollY();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Handle hash scrolling when component mounts
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, []);

  return (
    <div className={`min-h-screen relative transition-colors duration-500 ${
      theme === 'dark'
        ? 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 text-gray-100'
        : 'bg-gradient-to-br from-slate-200/50 via-slate-100/70 to-slate-200/50 text-slate-800'
    }`}>
    
    {/* Animated Background Elements */}
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      {/* Floating orbs */}
      <div className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-5 animate-float ${
        isDark ? 'bg-sunrise-orange' : 'bg-deep-blue'
      }`}></div>
      <div className={`absolute top-3/4 right-1/4 w-64 h-64 rounded-full opacity-5 animate-float delay-300 ${
        isDark ? 'bg-serengeti-gold' : 'bg-sunrise-orange'
      }`}></div>
      <div className={`absolute bottom-1/4 left-1/2 w-80 h-80 rounded-full opacity-5 animate-float delay-500 ${
        isDark ? 'bg-deep-blue' : 'bg-serengeti-gold'
      }`}></div>
      
      {/* Subtle pattern overlay */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, ${isDark ? 'white' : 'black'} 1px, transparent 0)`,
        backgroundSize: '60px 60px'
      }}></div>
    </div>

    <Navigation scrollY={scrollY} />
    <Hero />
    <About />
    <Services />
    <Achievements />
    <Experience />
    {/* <Testimonials /> */}
    <Contact />
    <Footer />
  </div>
  );
};

const AppContent: React.FC = () => {
  const scrollY = useScrollY();

  return (
    <Router>
      <Navigation scrollY={scrollY} />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogDetailWrapper />} />
      </Routes>
    </Router>
  );
};

const BlogDetailWrapper: React.FC = () => {
  const slug = window.location.pathname.split('/blog/')[1];
  return <BlogDetail slug={slug} />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
};

export default App;
